import { FunctionComponent, useEffect, useRef, useState, useContext } from "react";
import { useAppSelector } from "./store/hooks";
import { GameState } from "./store/gameStateSlice";
import { ActionsContext } from "./ActionsContext";
import { Player } from "./Types";
import { getClientPlayer } from "./Utils";
import { Timestamp } from "firebase/firestore";

const DISCONNECT_TIMEOUT_S = 60;

type Props = {
  disconnectedData: Record<string, any> | null;
};

/**
 * Показывает оверлей когда соперник отключился.
 * Даёт 60 секунд — потом победа за счёт неявки.
 */
const DisconnectOverlay: FunctionComponent<Props> = ({ disconnectedData }) => {
  const actions      = useContext(ActionsContext);
  const [players, currentPlayer, gameState, gameBoardState, matchScore] =
    useAppSelector(s => [s.players, s.currentPlayer, s.gameState, s.gameBoard, s.matchScore]);

  const [secondsLeft, setSecondsLeft] = useState(DISCONNECT_TIMEOUT_S);
  const [forfeitDone, setForfeitDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Безопасно определяем слот — только если оба игрока подключены
  const bothConnected = players.playerOne != null && players.playerTwo != null;

  let mySlot: "playerOne" | "playerTwo" | null = null;
  if (bothConnected) {
    try {
      mySlot = getClientPlayer(players) === Player.One ? "playerOne" : "playerTwo";
    } catch { mySlot = null; }
  }
  const opponentSlot = mySlot === "playerOne" ? "playerTwo" : mySlot === "playerTwo" ? "playerOne" : null;
  const opponentDisconnectedAt: Timestamp | null = opponentSlot
    ? (disconnectedData?.[opponentSlot] ?? null)
    : null;

  const isActive =
    bothConnected &&
    mySlot !== null &&
    !!opponentDisconnectedAt &&
    !forfeitDone &&
    gameState !== GameState.GameOverForfeit &&
    gameState !== GameState.GameOverBackgammon &&
    gameState !== GameState.GameOverGammon &&
    gameState !== GameState.GameOver;

  useEffect(() => {
    if (!isActive) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setSecondsLeft(DISCONNECT_TIMEOUT_S);
      return;
    }

    // Считаем сколько времени уже прошло с момента отключения
    const disconnectedMs = opponentDisconnectedAt!.toMillis?.() ?? Date.now();
    const elapsed = Math.floor((Date.now() - disconnectedMs) / 1000);
    const remaining = Math.max(0, DISCONNECT_TIMEOUT_S - elapsed);
    setSecondsLeft(remaining);

    if (remaining === 0) {
      doForfeit();
      return;
    }

    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          doForfeit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, opponentDisconnectedAt?.toMillis?.()]);

  const doForfeit = () => {
    if (forfeitDone || !bothConnected || mySlot === null) return;
    setForfeitDone(true);
    const winnerSlot = mySlot === "playerOne" ? Player.One : Player.Two;
    const pointsWon  = matchScore.pointsRequiredToWin;
    const newScore   = {
      ...matchScore,
      [winnerSlot]: matchScore[winnerSlot] + 1,
    };
    const loserSlot = winnerSlot === Player.One ? Player.Two : Player.One;
    actions.forfeitButtonClicked(gameBoardState, newScore, loserSlot).catch(() => {});
  };

  if (!isActive) return null;

  const urgent = secondsLeft <= 15;

  return (
    <div className="Disconnect-overlay">
      <div className="Disconnect-modal">
        <div className="Disconnect-icon">⚡</div>
        <div className="Disconnect-title">Соперник отключился</div>
        <div className="Disconnect-sub">
          Если он не вернётся, вы победите автоматически
        </div>
        <div className={`Disconnect-timer ${urgent ? "urgent" : ""}`}>
          {secondsLeft}
        </div>
        <div className="Disconnect-hint">секунд</div>
      </div>
    </div>
  );
};

export default DisconnectOverlay;
